/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// Core Libraries
// tslint:disable max-file-line-count
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Component, OnDestroy, OnInit, EventEmitter} from '@angular/core';
import {
  LineDeivceDetails,
  lineTypeEnum,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { ProductSelectorConfigData } from '@core/aem/interfaces/product-selector/product-selector.interface';
import { ProductSelectorService } from '@experience/shop/product-selector/services/product-selector.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PRODUCT_SELECTOR_ROUTES } from '@experience/shop/product-selector/constants/product-selector.constants';
import { ProductDetails } from '@experience/shop/product-details/interfaces/product-details.interface';
import { Subject } from 'rxjs';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { Cart } from '@support/api/proxies/responses/api-cart-response.interface';
import { APIError } from '@support/api/common/error.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { PRODUCT_OFFERINGS_CONSTANTS } from '@core/aem/components/product-offerings/product-offerings.constants';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { takeUntil, finalize } from 'rxjs/operators';
import { AddDeviceToNonLineItemsRequestModel } from 'app/model/add-nonline-device-request.model';
import { ProductInfo } from '@support/shared/interfaces/pdl-data.interface';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import {
  CART_EVENT_NAME,
  PDP_SUBTYPE_CONSTANT,
} from '@support/shared/constants/pdl-data.constants';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
// Settings
import { AppConfig } from '@root/app.config';

@Component({
  selector: 'tmo-selected-product-details',
  templateUrl: './selected-line-details.component.html',
})
export class LineDetailComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  public focusedTileIdDetails: LineDeivceDetails;
  public aemContent: ProductSelectorConfigData;
  public showSpinner: boolean;
  public isExistingLine: boolean;
  private ngUnsubscribe: Subject<void>;
  /**
   * Stores device details coming from API call
   */
  public deviceDetails: ProductDetails;
  /**
   * Holds true if i don't need a plan is selected
   */
  public noNeedPlan: boolean;
  /**
   * Holds true if plan or i dont need a plan is selected
   */
  public isPlanOrNoPlanSelected: boolean;
  /**
   * Stores whether cpo is clicked
   */
  public isCpoClicked: boolean;
  /**
   * Stores whether device has CPO
   */
  public hasCPO: boolean;
  /**
   * Function used to show or hide device carousel section
   */
  public isDeviceCarouselSectionEnabled: boolean;
  /**
   * Holds device sub type
   */
  public deviceSubType: string;
  /**
   * Flag for Assisted channel i.e. Retail & Care
   */
  // public isAssistedFlow: boolean;
  /**
   * Checks whether flow is byos
   */
  public isByos: boolean;
  /**
   * check if flow is byod
   */
  public isByod: boolean;
  /**
   * Holds boolean and set to true if max lines are reached
   */
  public isMaxlinesReached: boolean;
  /**
   * boolean to keep check if it is device upgrade flow
   */
  public isUpgrade: boolean;
  /**
   * Holds boolean if rep has permission to add second line or not
   */
  public hasPermissionToAddALine: boolean;
  /**
   * Holds boolean if rep has permission to add first line or not
   */
  public hasRepPermissionToAddFirstLine: boolean;
  /**
   * Flag for retail channel
   */
  // public isRetail: boolean;
  /**
   * Hold pdl related details to trigger event
   */
  public pdlData: object;
  public isEdit: boolean;
  /**
   * Flag for Web channel
   */
  // private isWebChannel: boolean;
  public isNewProductSelectorTemplateEnabled: boolean;
  public isLimitedPlanSelected: boolean;
  public serviceSuccess: boolean;
  public disclaimer: string;
  public isPlanFirst: boolean;
  public isZipCodeSet: boolean;
  /**
   * check if user authenticated
   */
  public isAuthFlow: boolean;
  public isPlanSectionEnabled: boolean;
  /**
   * check if new plans page is enabled
   */
  public isNewPlansPageEnabled: boolean;
  /**
   * Flag for Connect Brand
   */
  public isConnectBrand: boolean;
  /**
   * Flag to fetch wheater to hide service section or not
   */
  public ishideServiceSection: boolean;
  /**
   * flag to skip service
   */
  public isNotShowService: boolean = true;
  /**
   * flag to enable Auto Pay Discount Details
   */
  public autoPayDiscountEnabled: boolean;
  /**
   * Auto Pay discoutn details modal
   */
  public autopayDiscountDetailsModal: StaticModal;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService,
    private stateUtilService: StateStoreUtilsService,
    private productSelectorService: ProductSelectorService,
    private tileManagementService: TileManagementService,
    public routeSnapshot: ActivatedRoute,
    private pdpDataHandlerService: PdpDataHandlerService,
    public pdlService: PdlService,
    private featureToggleUtilService: FeatureToggleUtilService,
    private appConfig: AppConfig,
  ) {
    super(pdlService, route);
    this.ngUnsubscribe = new Subject<void>();
    this.deviceSubType = HARDGOODS_CONSTANTS.BROWSE_DEVICES;
    this.hasPermissionToAddALine = this.cartSupportService.checkForAddALinePermission(
      '/product-selector',
      PERMISSION_CONSTANTS.ADD_A_LINE
    );
    this.hasRepPermissionToAddFirstLine =
      this.cartSupportService.checkForAddALinePermission('/product-selector');
    this.isNewProductSelectorTemplateEnabled =
      this.featureToggleUtilService.isNewProductSelectorTemplateEnabled();
    this.isLimitedPlanSelected = false;
    this.serviceSuccess = false;
    this.autopayDiscountDetailsModal = {
      disableDismiss: false,
      disableEsc: false,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'autopay-discount-info-modal',
    };
  }

  /**
   * Loads the initial configuration of Product Selector Page
   * @returns void
   */
  public ngOnInit(): void {
    this.initAEMData();
    this.showToGoToByodOrPhones();
    this.displayCarousel();
    // this.isRetail = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL;
    // this.isWebChannel = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB;
    const offerId = this.stateUtilService.getSelectedOfferId();
    const productDetails = this.stateUtilService.getSelectedDeviceDetails(offerId);
    if (productDetails) {
      this.deviceDetails = productDetails.getProductDetails();
      this.hasCPO = productDetails.hasCPO;
      this.isCpoClicked = productDetails.getCpoClicked();
    }
    this.getUpdatedFocusedTileDetails();
    this.getTileInFouusDetails();
    // this.isAssistedFlow = SETTINGS.CHANNEL.toLowerCase() !== CHANNELS.WEB;
    this.isUpgrade = this.stateUtilService.getUpgradeFlow();
    this.isEdit = !!this.route.snapshot.queryParams.isEdit;
    const guestCheck =
      this.stateUtilService.getCustomerId() === SHARED_BROWSE_CONSTANTS.ANON ||
      this.stateUtilService.getCustomerId() === SHARED_BROWSE_CONSTANTS.EMPTY_STRING;
    this.isAuthFlow =
      this.stateUtilService.getCustomerId() && !this.stateUtilService.getAddFan() && !guestCheck;
    this.isNewPlansPageEnabled = this.stateUtilService.getNewPlansPage();
    this.isConnectBrand = document.cookie.includes('brand=connect');
    this.autoPayDiscountEnabled = !!this.appConfig.getConfig('autoPayDiscountEnabled');
  }
  /**
   * Retrieves AEM content for product selector landing component
   * @returns {void}
   */
  private initAEMData(): void {
    this.productSelectorService
      .getProductSelectorContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: ProductSelectorConfigData) => {
        this.showSpinner = false;
        this.aemContent = this.objectService.get(data, 'configurableData');
        this.autopayDiscountDetailsModal.headerText = this.aemContent.autopayDiscountInfoModalHeader;
        this.autopayDiscountDetailsModal.bodyText = this.aemContent.autopayDiscountInfoModalBody;
        this.disclaimer = this.objectService.get(this.aemContent, 'limitedPlanDisclaimer', '');
        this.ishideServiceSection =
          this.isNotShowService && !this.route.snapshot.queryParams.isServiceEditable
            ? true
            : false;
      });
  }

  /**
   * Function to update if no plan checkbox is checked
   * @param {boolean} value
   */
  // tslint:disable cyclomatic-complexity
  public updateNoPlanSelected(value: boolean): void {
    const isPlanExist = !!this.objectService.get(this.focusedTileIdDetails, 'plansFromCart.id');
    const isDeviceExist = !!(
      this.objectService.get(this.focusedTileIdDetails, 'selectedDevice') ||
      this.objectService.get(this.focusedTileIdDetails, 'byodSimData.simDescription')
    );
    switch (true) {
      case value && isPlanExist && !isDeviceExist:
        this.deleteLine(value);

        // Make 2106 API call
        break;
      case value && !isPlanExist && isDeviceExist:
        this.tileManagementService.setIsPlanNeeded(value);
        this.tileManagementService.getUpdatedData();
        if (this.isNewProductSelectorTemplateEnabled) {
          this.goToCart();
        }
        break;
      case value && isPlanExist && isDeviceExist:
        this.updateDeviceToCartLevel(value);
        break;
      case value && !isPlanExist && !isDeviceExist:
        this.tileManagementService.deleteLine();
        this.tileManagementService.createTile(lineTypeEnum.NONLINE);
        this.tileManagementService.setIsPlanNeeded(value);
        this.tileManagementService.getUpdatedData();
        // to hide device blade data
        this.isPlanOrNoPlanSelected = false;
        break;
      case !value && !isPlanExist && isDeviceExist:
        this.tileManagementService.setIsPlanNeeded(value);
        this.tileManagementService.getUpdatedData();
        break;
      case !value && !isPlanExist && !isDeviceExist:
        this.tileManagementService.deleteLine();
        this.tileManagementService.createTile();
        this.tileManagementService.setIsPlanNeeded(value);
        this.tileManagementService.getUpdatedData();
        break;
      default:
    }
    this.noNeedPlan = value;
  }

  /**
   * Function used to uncheck i dont need a plan if coming from scan to cart scenario.
   */
  public setIDontPlanToFalse(): void {
    const existingTilesScanedInCR = this.stateUtilService.getTileIdList(lineTypeEnum.SCANTOCART);
    const tileIdToBeRemoved = existingTilesScanedInCR.findIndex((tileId: string) => {
      return tileId === this.focusedTileIdDetails.tileId;
    });
    if (tileIdToBeRemoved > -1 && this.hasRepPermissionToAddFirstLine) {
      existingTilesScanedInCR.splice(tileIdToBeRemoved, 1);
      if (this.objectService.get(existingTilesScanedInCR, 'length') > 0) {
        this.stateUtilService.setTileIdList(existingTilesScanedInCR, lineTypeEnum.SCANTOCART);
      } else {
        this.stateUtilService.clearTileIdList(lineTypeEnum.SCANTOCART);
      }
      this.tileManagementService.setIsPlanNeeded(false);
    }
  }

  /**
   * Function to get Line details for tile in focus
   */
  private getTileInFouusDetails(): void {
    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: Params) => {
      this.tileManagementService.setTileIdInFocus(value.tileId);
      this.focusedTileIdDetails = this.tileManagementService.getFocusedTileIdDetails();
      this.isPlanFirst =
        !!this.objectService.get(this.focusedTileIdDetails, 'plansFromCart.id') &&
        !!!(
          this.objectService.get(this.focusedTileIdDetails, 'selectedDevice') ||
          this.objectService.get(this.focusedTileIdDetails, 'byodSimData.simDescription')
        );
      const simData = this.objectService.get(this.focusedTileIdDetails, 'byodSimData');
      const isCmmOrIMei = this.objectService.get(this.focusedTileIdDetails, 'byodCMMOrImei');
      this.isByos = simData && simData.simNumber && isCmmOrIMei ? true : false;
      this.isByod = simData && simData.simSKU && isCmmOrIMei;
      this.isExistingLine =
        this.objectService.get(this.focusedTileIdDetails, 'lineTileType') === lineTypeEnum.EXISTING;
      this.setIDontPlanToFalse();
      const deviceType = this.objectService.get(
        this.focusedTileIdDetails,
        'deviceDetails.productSubType'
      );
      if (deviceType) {
        this.deviceSubType =
          deviceType.toUpperCase() === HARDGOODS_CONSTANTS.HANDSET
            ? HARDGOODS_CONSTANTS.BROWSE_DEVICES
            : deviceType.toUpperCase() === HARDGOODS_CONSTANTS.MOBILEINTERNET
              ? HARDGOODS_CONSTANTS.BROWSE_TABLETS
              : '';
      }
    });
  }

  private updateDeviceToCartLevel(value: boolean): void {
    this.showSpinner = true;
    let products;
    // if (this.isWebChannel) {
    const cartRef = this.stateUtilService.getCart();
    const lineNumber = this.objectService.get(this.focusedTileIdDetails, 'tileId').slice(-1);
    const selectedDeviceSku = this.objectService.get(
      this.focusedTileIdDetails,
      'selectedDevice.sku'
    );
    products = this.pdpDataHandlerService
      .getRemovedItemDetails(PDP_SUBTYPE_CONSTANT.LINENUMBER, lineNumber, cartRef)
      .filter((product: ProductInfo) => product.productInfo.id !== selectedDeviceSku);
    // }

    this.cartSupportService
      .updateLineLevelDeviceToCartLevel(
        this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.cartItemId')
      )
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (data: Cart) => {
          this.tileManagementService.deleteLine();
          this.tileManagementService.createTile(lineTypeEnum.NONLINE);
          this.tileManagementService.setNonLineItemDetails(data);
          this.tileManagementService.mapToCartResponse(data);
          this.tileManagementService.setIsPlanNeeded(value);
          this.tileManagementService.getUpdatedData();
          this.triggerCartEvent(
            data,
            products,
            CART_EVENT_NAME.CARTREMOVE,
            this.aemContent.continue,
            this.objectService.get(this.aemContent, 'areYouSure', '')
          );
          this.router.navigate([
            PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR,
            this.stateUtilService.getFocusedTileId(),
          ]);
          this.showSpinner = false;
          if (this.isNewProductSelectorTemplateEnabled && value) {
            this.goToCart();
          }
        },
        (error: APIError) => {
          this.showSpinner = false;
          this.noNeedPlan = !value;
          if (this.isNewProductSelectorTemplateEnabled && value) {
            this.goToCart();
          }
        }
      );
  }

  /**
   * Function used to delete line
   */
  private deleteLine(value: boolean): void {
    this.showSpinner = true;
    this.cartSupportService
      .deleteFocusedLine()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data: Cart) => {
          this.showSpinner = false;
          this.tileManagementService.deleteLine();
          if (data) {
            this.tileManagementService.mapToCartResponse(data);
          }
          const tileLength = this.stateUtilService.getTileIdList(lineTypeEnum.LINE).length;
          if (tileLength === 0) {
            this.tileManagementService.createTile();
            this.tileManagementService.setIsPlanNeeded(value);
          } else {
            this.tileManagementService.setIsPlanNeeded(false);
          }
          this.tileManagementService.getUpdatedData(tileLength === 1 || tileLength === 0);
          this.isPlanOrNoPlanSelected = false;
          this.router.navigate([
            PRODUCT_OFFERINGS_CONSTANTS.NAVIGATE_TO_PRODUCT_SELECTOR,
            this.stateUtilService.getFocusedTileId(),
          ]);
        },
        (error: APIError) => {
          this.showSpinner = false;
        }
      );
  }

  /**
   * Destroys all the subscriptions
   * @returns {void}
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stateUtilService.setNewPlansPage(false);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * function gotoCart for Lines
   * @method goToCart()
   * @param changeSaved boolean indicates if the changes are saved or not.
   * @returns void
   */
  public goToCart(changeSaved?: boolean): void {
    let params;
    const isUpgrade = this.objectService.get(this.focusedTileIdDetails, 'isUpgrade');
    if (this.isNewProductSelectorTemplateEnabled && isUpgrade) {
      params = { queryParams: { isUpgrade: true } };
      this.router.navigate([PRODUCT_SELECTOR_ROUTES.CART], params);
    } else {
      this.router.navigate([PRODUCT_SELECTOR_ROUTES.CART]);
    }
    // this.router.navigate([PRODUCT_SELECTOR_ROUTES.CART]);
  }

  private getUpdatedFocusedTileDetails(): void {
    this.tileManagementService.focusedTileDetails
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: LineDeivceDetails) => {
        this.focusedTileIdDetails = data;
        this.showToGoToByodOrPhones();
        this.displayCarousel();
        this.isExistingLine =
          this.objectService.get(this.focusedTileIdDetails, 'lineTileType') ===
          lineTypeEnum.EXISTING;
        this.isMaxlinesReached = this.cartSupportService.isMaxLineReached();
      });
  }

  /**
   * Function to show or hide radio button
   * @returns {boolean}
   */
  public showToGoToByodOrPhones(): void {
    const planName = !!this.objectService.get(this.focusedTileIdDetails, 'plansFromCart.id');
    const noPlanNeeded = !!this.objectService.get(this.focusedTileIdDetails, 'noPlanNeeded');
    const isDeviceSimExist = !!(
      this.objectService.get(this.focusedTileIdDetails, 'selectedDevice') ||
      this.objectService.get(this.focusedTileIdDetails, 'byodSimData.cartItemId') ||
      this.objectService.get(this.focusedTileIdDetails, 'byodSimData.simNumber')
    );
    this.isPlanOrNoPlanSelected = (planName || noPlanNeeded) && !isDeviceSimExist;
  }

  /**
   * Function used to navigate to PDP
   */
  public navigateToDevicePdp(): void {
    const color = this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.color');
    this.stateUtilService.setSelectedColor(color);
    this.router.navigate(
      [
        HARDGOODS_CONSTANTS.PRODUCT_DETAILS,
        this.focusedTileIdDetails.selectedDevice.canonicalUrl,
        color,
        this.focusedTileIdDetails.selectedDevice.memory,
      ],
      {
        queryParams: {
          allowEdit: true,
          isEditingUpgradeLine: this.focusedTileIdDetails.isUpgrade,
          isEditingNonLineDevice: this.isUpgrade && !this.focusedTileIdDetails.isUpgrade,
        },
      }
    );
  }

  /**
   * Method to navigate to PDP
   */
  public goToPdp(isRefurbished: boolean): void {
    const color = this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.color');
    const offerId = this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.offerId');
    const memory = this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.memory');
    this.router.navigate(
      [HARDGOODS_CONSTANTS.PRODUCT_DETAILS, { device: offerId, color: color, memory: memory }],
      { queryParams: { isRefurbished: isRefurbished } }
    );
  }

  /**
   * Function used to display or hide device section
   */
  private displayCarousel(): void {
    this.isDeviceCarouselSectionEnabled = !(
      this.objectService.get(this.focusedTileIdDetails, 'byodSimData.cartItemId') ||
      this.objectService.get(this.focusedTileIdDetails, 'byodSimData.simNumber') ||
      this.objectService.get(this.focusedTileIdDetails, 'selectedDevice.offerId')
    );
  }

  /**
   *
   * @param {string} deviceCondition
   */
  public getRefurbishedOrNewDevice(deviceCondition: string): void {
    if (this.objectService.get(this.focusedTileIdDetails, 'lineTileType') === lineTypeEnum.LINE) {
      this.replaceDeviceToLine();
    } else {
      this.replaceCartLevelDevice();
    }
  }

  /**
   * Function used to replace selected device.
   */
  private replaceDeviceToLine(): void {
    this.cartSupportService
      .updateLineDevice(this.focusedTileIdDetails.selectedDevice.sku)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (cart: Cart) => {
          this.tileManagementService.mapToCartResponse(cart);
          this.showSpinner = false;
        },
        (error: APIError) => {
          this.showSpinner = false;
        }
      );
  }

  /**
   * Function used to replace and add device as non line item
   */
  private replaceCartLevelDevice(): void {
    const skuItemCollection = [];
    const skuItem = new AddDeviceToNonLineItemsRequestModel();
    skuItem.setSkuDetails(
      this.focusedTileIdDetails.selectedDevice.sku,
      this.focusedTileIdDetails.selectedDevice.offerId
    );
    skuItemCollection.push(skuItem.getDeviceInfo());
    this.cartSupportService
      .postDeviceAsNonLineItem(skuItemCollection, true)
      .pipe(
        takeUntil(this.ngUnsubscribe.asObservable()),
        finalize(() => {
          this.showSpinner = false;
        })
      )
      .subscribe(
        (cartData: Cart) => {
          this.tileManagementService.setNonLineItemDetails(cartData);
          this.showSpinner = false;
        },
        (error: APIError) => {
          this.showSpinner = false;
        }
      );
  }

  /**
   * Method to set data for cart events and trigger cart events (cartRemove, cartAdd)
   * @returns void
   */
  private triggerCartEvent(
    cart?: Cart,
    productInfo?: ProductInfo[],
    action?: string,
    ctaName?: string,
    modalName?: string
  ): void {
    // if (this.isWebChannel) {
    this.pdlData = {
      ctaName: ctaName,
      product: productInfo,
    };
    if (modalName) {
      this.pdlData[`modalName`] = modalName;
    }
    const isCartEmpty = !this.cartSupportService.isCartNotEmpty(cart);
    this.pdpDataHandlerService.triggerCartEvent(cart, this.pdlPageComponentId, isCartEmpty, action);
    // }
  }

  public hideServiceSection(event: boolean): void {
    this.isLimitedPlanSelected = event;
  }

  public plansSectionCollapse(serviceSuccess: boolean): void {
    this.serviceSuccess = serviceSuccess;
    this.isPlanSectionEnabled = true;
    if (!!this.route.snapshot.queryParams.isPlanEdit) {
      this.serviceSuccess = false;
    }
    if (this.serviceSuccess) {
      this.isNewPlansPageEnabled = false;
      this.stateUtilService.setNewPlansPage(false);
    }
  }

  public changeDisclaimer(planFamily: string): void {
    switch (true) {
      case planFamily === SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN:
        this.disclaimer = this.objectService.get(this.aemContent, 'limitedPlanDisclaimer', '');
        break;
      case planFamily === SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMOBILE:
        this.disclaimer = this.objectService.get(this.aemContent, 'limitedPlanDisclaimer', '');
        break;
      case planFamily === SHARED_BROWSE_CONSTANTS.MOBILE_INTERNET_PLAN:
        this.disclaimer = this.objectService.get(this.aemContent, 'onAllTmobilePlans', '');
        break;
      default:
        break;
    }
  }

  public showPlanSection(isPlanSectionEnabled: boolean): void {
    this.isPlanSectionEnabled = isPlanSectionEnabled;
  }

  /**
   * Toggle AutoPay Discount details Modal display
   * @param openModal 
   */
  public toggleAutopayDiscountDetailsModal(openModal: boolean): void {
    this.autopayDiscountDetailsModal.toggleModalEmitter.emit({open: openModal, close: !openModal});
  }
}
